import * as C from '../../../../../ui/common/CommonStyles';
import * as M from '@mantine/core';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { modelFilterHook } from '../../../../../../shared/utils/modelFilter';
import { handleChangeLocaleTime } from '../../../../../../shared/utils/time';
import { cleanParams } from '../../../../../../shared/utils/cleanParams';
import { useDispatchList } from '../hooks/useDispatchList';
import { SelectAllRowsParams } from '../../../../../../shared/hook/useCheckbox';
import { IDispatch } from '../types';

interface IPropsDispatchTable {
  initSearchType?: string;
  selectedIds: Set<number>;
  onSelectRow: (id: number) => void;
  onSelectAllRows: (params: SelectAllRowsParams<IDispatch>) => void;
  deviceCd: string;
  deviceSubCd: string;
}

const tableHead = [
  '번호',
  'MAC',
  '모델명',
  'S/N',
  '출고 거래처(From)',
  '입고 거래처(To)',
  '서비스 사용 여부',
  '메모',
  '최종 수정자',
  '최종 수정일자',
];

export default function DispatchTable(props: IPropsDispatchTable) {
  const { initSearchType, selectedIds, onSelectRow, onSelectAllRows, deviceCd, deviceSubCd } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams('');

  const size = 10;
  const page = searchParams.get('page') || '1';
  const searchTypeUrl = searchParams.get('searchType') || initSearchType;
  const searchValueUrl = searchParams.get('keyword') || '';

  const currentPathname = useLocation().pathname;
  const isEpTp = currentPathname.includes('eptp');
  const isManufacturer = currentPathname.includes('manufacturer');
  const isEndUser = currentPathname.includes('end-user');

  const { dispatches, totalCount } = useDispatchList({
    page: Number(page),
    size,
    channel: isEpTp ? 'MFM' : isManufacturer ? 'SCAUTR' : isEndUser ? 'PROGIX' : '',
    searchType: searchTypeUrl?.toUpperCase() || '',
    searchValue: searchValueUrl || '',
    deviceTypeCd: deviceCd,
    deviceSubTypeCd: deviceSubCd,
  });

  return (
    <>
      <M.Table.ScrollContainer minWidth={1200}>
        <M.Table>
          <M.Table.Thead>
            <M.Table.Tr>
              {!isEndUser && (
                <M.Table.Th>
                  <M.Checkbox
                    checked={
                      selectedIds.size > 0 &&
                      dispatches
                        .filter((item) => item.registrationStatus.toUpperCase() !== 'REGISTERED')
                        .every((item) => selectedIds.has(item.id))
                    }
                    onChange={(event) =>
                      onSelectAllRows({
                        items: dispatches,
                        checked: event.currentTarget.checked,
                        filterFn: (item) => item.registrationStatus.toUpperCase() !== 'REGISTERED',
                      })
                    }
                  />
                </M.Table.Th>
              )}
              {tableHead.map((head) => (
                <M.Table.Th key={head} align="center">
                  {head}
                </M.Table.Th>
              ))}
              {isEndUser && <M.Table.Th>서비스 등록 상태</M.Table.Th>}
            </M.Table.Tr>
          </M.Table.Thead>
          <M.Table.Tbody>
            {dispatches.length ? (
              dispatches.map((data, index) => (
                <M.Table.Tr key={data.id}>
                  {!isEndUser && (
                    <M.Table.Td>
                      <M.Checkbox
                        disabled={data.registrationStatus.toUpperCase() === 'REGISTERED'}
                        checked={selectedIds.has(data.id)}
                        onChange={() => onSelectRow(data.id)}
                      />
                    </M.Table.Td>
                  )}
                  <M.Table.Td>{data.id}</M.Table.Td>
                  <M.Table.Td
                    style={{
                      ...(!isEndUser && {
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }),
                    }}
                    onClick={() => {
                      if (!isEndUser) {
                        navigate(`${currentPathname}/register?id=${data.id}`);
                      }
                    }}
                  >
                    {data.mac}
                  </M.Table.Td>
                  <M.Table.Td>{modelFilterHook(data.deviceTypeCd, data.deviceSubTypeCd)}</M.Table.Td>
                  <M.Table.Td>{data.serialNumber}</M.Table.Td>
                  <M.Table.Td>{data.fromCompanyId ? `(${data.fromCompanyId}) ${data.fromCompanyName}` : ''}</M.Table.Td>
                  <M.Table.Td>{data.toCompanyId ? `(${data.toCompanyId}) ${data.toCompanyName}` : ''}</M.Table.Td>
                  <M.Table.Td>{data.registrationStatus.toUpperCase() === 'REGISTERED' ? '사용' : '미사용'}</M.Table.Td>
                  <M.Table.Td>{data.memo}</M.Table.Td>
                  <M.Table.Td>{data.modUserName}</M.Table.Td>
                  <M.Table.Td>{handleChangeLocaleTime(data.modDt)?.substring(0, 16)}</M.Table.Td>
                  {isEndUser && (
                    <M.Table.Td>
                      {data.registrationStatus.toUpperCase() === 'UNREGISTERED'
                        ? '미등록'
                        : data.registrationStatus.toUpperCase() === 'PENDING'
                          ? '승인 대기'
                          : '등록 완료'}
                    </M.Table.Td>
                  )}
                </M.Table.Tr>
              ))
            ) : (
              <M.Table.Tr>
                <M.Table.Td colSpan={tableHead.length + (isEndUser ? 1 : 0)}>
                  <M.Text align="center" size="sm">
                    데이터가 없습니다.
                  </M.Text>
                </M.Table.Td>
              </M.Table.Tr>
            )}
          </M.Table.Tbody>
        </M.Table>
      </M.Table.ScrollContainer>
      <C.PaginationBox>
        <M.Pagination
          total={Math.ceil(totalCount / size)}
          value={Number(page)}
          onChange={(value: number) => {
            const currentParams = Object.fromEntries(searchParams.entries());
            setSearchParams(
              cleanParams({
                ...currentParams,
                page: String(value),
              }),
            );
          }}
        />
      </C.PaginationBox>
    </>
  );
}
